import React, { useEffect, useMemo } from "react";
import {
    Alert,
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Select,
    Space,
    Spin,
    Switch,
    Typography,
    message,
} from "antd";
import { ExperimentOutlined } from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import {
    useGetSystemSettingsByCategoryQuery,
    useUpdateSystemSettingsBatchMutation,
} from "../../services/nestjs-base-api";

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const KEYS = {
    enabled: "KNOWLEDGE_DISTILL_ENABLED",
    primaryProvider: "KNOWLEDGE_DISTILL_PRIMARY_PROVIDER",
    primaryModel: "KNOWLEDGE_DISTILL_PRIMARY_MODEL",
    primaryTemperature: "KNOWLEDGE_DISTILL_PRIMARY_TEMPERATURE",
    fallbackProvider: "KNOWLEDGE_DISTILL_FALLBACK_PROVIDER",
    fallbackModel: "KNOWLEDGE_DISTILL_FALLBACK_MODEL",
    fallbackTemperature: "KNOWLEDGE_DISTILL_FALLBACK_TEMPERATURE",
    systemPrompt: "KNOWLEDGE_DISTILL_SYSTEM_PROMPT",
    userPromptTemplate: "KNOWLEDGE_DISTILL_USER_PROMPT_TEMPLATE",
};

const PROVIDERS = [
    { label: "Anthropic", value: "anthropic" },
    { label: "OpenAI", value: "openai" },
    { label: "Google", value: "google" },
];

/**
 * SYR-78 — standalone Configure page for the Knowledge Distill AI (cleans raw
 * ingested doc/scrape text into brand context). Reads/writes KNOWLEDGE_DISTILL_*
 * system settings; usage is recorded under the 'knowledge_distill' feature.
 */
export const KnowledgeDistillConfigurePage: React.FC = () => {
    const { user } = useAuth();
    const jwt = user?.jwtToken || "";
    const [form] = Form.useForm();

    const { data, isLoading } = useGetSystemSettingsByCategoryQuery({
        jwtToken: jwt,
    });
    const [saveBatch, { isLoading: isSaving }] =
        useUpdateSystemSettingsBatchMutation();

    const settingsMap = useMemo(() => {
        const map: Record<string, string> = {};
        (data?.categories || []).forEach((cat) =>
            cat.settings.forEach((s) => {
                map[s.name] = s.value;
            })
        );
        return map;
    }, [data]);

    useEffect(() => {
        if (!data) return;
        form.setFieldsValue({
            enabled: settingsMap[KEYS.enabled] !== "false",
            primaryProvider: settingsMap[KEYS.primaryProvider] || "anthropic",
            primaryModel: settingsMap[KEYS.primaryModel] || "",
            primaryTemperature: parseFloat(
                settingsMap[KEYS.primaryTemperature] || "0.1"
            ),
            fallbackProvider: settingsMap[KEYS.fallbackProvider] || "openai",
            fallbackModel: settingsMap[KEYS.fallbackModel] || "",
            fallbackTemperature: parseFloat(
                settingsMap[KEYS.fallbackTemperature] || "0.1"
            ),
            systemPrompt: settingsMap[KEYS.systemPrompt] || "",
            userPromptTemplate: settingsMap[KEYS.userPromptTemplate] || "",
        });
    }, [data, settingsMap, form]);

    const onFinish = async (values: any) => {
        if (!values.userPromptTemplate?.includes("{raw_text}")) {
            message.error("The user prompt template must include {raw_text}.");
            return;
        }
        const settings = [
            { name: KEYS.enabled, value: values.enabled ? "true" : "false" },
            { name: KEYS.primaryProvider, value: values.primaryProvider },
            { name: KEYS.primaryModel, value: values.primaryModel },
            {
                name: KEYS.primaryTemperature,
                value: String(values.primaryTemperature),
            },
            { name: KEYS.fallbackProvider, value: values.fallbackProvider },
            { name: KEYS.fallbackModel, value: values.fallbackModel },
            {
                name: KEYS.fallbackTemperature,
                value: String(values.fallbackTemperature),
            },
            { name: KEYS.systemPrompt, value: values.systemPrompt },
            { name: KEYS.userPromptTemplate, value: values.userPromptTemplate },
        ];
        try {
            await saveBatch({ jwtToken: jwt, payload: { settings } }).unwrap();
            message.success("Knowledge Distill AI settings saved");
        } catch (err: any) {
            message.error(err?.data?.message || "Could not save settings");
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: "center" }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: 24, maxWidth: 900 }}>
            <Title level={3} style={{ marginTop: 0 }}>
                <ExperimentOutlined style={{ marginRight: 8 }} />
                Knowledge Distill AI
            </Title>
            <Paragraph type="secondary">
                Cleans raw text extracted from uploaded documents and website
                scrapes into focused, durable brand context before it grounds the
                AI. Runs automatically on ingest when enabled. Token usage is
                recorded under the <code>knowledge_distill</code> feature.
            </Paragraph>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Card style={{ marginBottom: 16 }}>
                    <Form.Item
                        name="enabled"
                        label="Enabled"
                        valuePropName="checked"
                        extra="When off, raw extracted text is stored as-is (no AI cleanup)."
                    >
                        <Switch />
                    </Form.Item>
                </Card>

                <Card title="Model" style={{ marginBottom: 16 }}>
                    <Space size="large" wrap>
                        <Form.Item name="primaryProvider" label="Primary provider">
                            <Select options={PROVIDERS} style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item name="primaryModel" label="Primary model">
                            <Input style={{ width: 280 }} />
                        </Form.Item>
                        <Form.Item
                            name="primaryTemperature"
                            label="Primary temperature"
                        >
                            <InputNumber min={0} max={1} step={0.05} />
                        </Form.Item>
                    </Space>
                    <Space size="large" wrap>
                        <Form.Item name="fallbackProvider" label="Fallback provider">
                            <Select options={PROVIDERS} style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item name="fallbackModel" label="Fallback model">
                            <Input style={{ width: 280 }} />
                        </Form.Item>
                        <Form.Item
                            name="fallbackTemperature"
                            label="Fallback temperature"
                        >
                            <InputNumber min={0} max={1} step={0.05} />
                        </Form.Item>
                    </Space>
                </Card>

                <Card title="Prompts" style={{ marginBottom: 16 }}>
                    <Form.Item
                        name="systemPrompt"
                        label="System prompt"
                        extra="Instructions for what to keep vs. strip."
                    >
                        <TextArea rows={12} style={{ fontFamily: "monospace" }} />
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="The user prompt template must include the {raw_text} placeholder, which is replaced with the extracted text."
                    />
                    <Form.Item name="userPromptTemplate" label="User prompt template">
                        <TextArea rows={4} style={{ fontFamily: "monospace" }} />
                    </Form.Item>
                </Card>

                <Button type="primary" htmlType="submit" loading={isSaving}>
                    Save
                </Button>
            </Form>
        </div>
    );
};
